/**
 * EditPanelProposalCard.tsx
 *
 * Inline panel proposal card rendered inside the AI message bubble (mockup 18).
 * Appears when the AI can fulfill a chat request with a new panel (action='add')
 * or a modification of an existing panel (action='modify').
 *
 * Brand guide §28 — AI panel proposal card.
 *
 * Accessibility:
 *  - role="region" with a context-appropriate aria-label:
 *      - "Proposed new panel preview" for action='add'
 *      - "Proposed panel modification preview" for action='modify'
 *  - "Add to dashboard" / "Apply change" and "Dismiss" buttons have descriptive
 *    aria-labels.
 *  - focus-visible:ring-2 focus-visible:ring-focusRing on all interactive elements.
 */

import React from 'react'
import { BarChart2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { EditProposal } from '@/types/DashboardV1.types'

interface EditPanelProposalCardProps {
  proposal: EditProposal
  onAccept: () => void
  onDismiss: () => void
}

/**
 * Mini bar preview — renders up to 4 rows from the proposal preview data.
 * Uses the same visual pattern as the mockup 18 proposal card.
 */
const MiniBarPreview: React.FC<{
  labels: string[]
  values: number[]
}> = ({ labels, values }) => {
  if (labels.length === 0) return null

  const maxVal = Math.max(...values, 1)
  const rows = labels.slice(0, 4)

  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((label, i) => {
        const pct = Math.round((values[i] ?? 0) / maxVal * 100)
        return (
          // Key must include index — labels may not be unique across rows
          // (e.g., duplicate session names coming back from the AI).
          <div key={`${label}-${i}`} className="flex items-center gap-1.5">
            <span className="text-[10px] text-secondary w-16 flex-shrink-0 text-right truncate">
              {label}
            </span>
            <div className="flex-1 h-2 bg-muted rounded-sm overflow-hidden">
              <div
                className="h-full bg-accent rounded-sm opacity-70"
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
            </div>
            <span className="text-[10px] text-primary font-semibold w-6 text-right flex-shrink-0">
              {values[i] ?? 0}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export const EditPanelProposalCard: React.FC<EditPanelProposalCardProps> = ({
  proposal,
  onAccept,
  onDismiss,
}) => {
  const isAdd = proposal.action === 'add'
  const headerLabel = isAdd
    ? `New panel: ${proposal.panelTitle}`
    : `Modified: ${proposal.panelTitle}`

  return (
    <div
      className="mt-2.5 border-2 border-accent rounded-xl overflow-hidden bg-card"
      role="region"
      aria-label={isAdd ? 'Proposed new panel preview' : 'Proposed panel modification preview'}
    >
      {/* Card header — accent-bg with chart icon */}
      <div className="flex items-center gap-2 px-3 py-2 bg-accent-background border-b border-accent/30">
        <BarChart2 size={12} className="text-accent flex-shrink-0" aria-hidden="true" />
        <span className="text-xs font-semibold text-accent truncate">{headerLabel}</span>
      </div>

      {/* Mini chart preview */}
      <div className="px-3 py-2.5">
        <p className="text-[11px] font-semibold text-primary mb-2">
          {proposal.panelTitle} · {proposal.panelType.replace('_', ' ')}
        </p>
        <MiniBarPreview
          labels={proposal.previewLabels}
          values={proposal.previewValues}
        />
        {proposal.subtitle && (
          <p className="text-[10.5px] text-muted-foreground mt-2 pt-2 border-t border-border">
            {proposal.subtitle}
          </p>
        )}
      </div>

      {/* Accept / Dismiss actions */}
      <div className="flex gap-1.5 px-3 py-2 border-t border-border bg-muted">
        <Button
          size="sm"
          className="flex-1 text-xs font-semibold focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
          onClick={onAccept}
          // Accessible name mirrors the visible label for each variant so screen
          // reader users and sighted users get the same action phrasing
          // (WCAG 2.5.3 Label in Name).
          aria-label={
            isAdd
              ? `Add ${proposal.panelTitle} panel to your dashboard`
              : `Apply changes to ${proposal.panelTitle} panel`
          }
        >
          <Plus size={12} className="mr-1 flex-shrink-0" aria-hidden="true" />
          {isAdd ? 'Add to dashboard' : 'Apply change'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-xs focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
          onClick={onDismiss}
          aria-label="Dismiss this panel suggestion"
        >
          Dismiss
        </Button>
      </div>
    </div>
  )
}
